import { useState, useRef, useEffect, lazy, Suspense, type PointerEvent as ReactPointerEvent } from 'react';
import { Routes, Navigate, Outlet, useLocation } from 'react-router-dom';
import { TooltipProvider } from '@pinpoint/ui/tooltip';
import { AuthProvider } from './auth/AuthContext';
import { useAuth } from './auth/authHooks';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { AccessPendingRoute, ProductNotFound, ProductRecoveryScreen } from './errors/ProductRouteStates';
import { defineRouteBinding, renderRouteManifest } from './routes/renderRouteManifest';
import { classifyBeforeAuth, selectProductRouteProfile } from './routes/productRouteProfile';
import { ProductAnalyticsOwner } from './analytics/ProductAnalyticsOwner';
import { AuthenticatedRouteHeadingFocus } from './navigation/RouteHeadingFocus';
import { ProductNavigationProvider } from './navigation/ProductNavigation';
import { isLatestProductionHost } from '../../lib/productHostPolicy';
import { clearSiteData, lazyImportForRecovery } from '../../lib/deployChunkRecovery';
import LoginPage from './auth/LoginPage';
import { startAuthenticatedRouteHandoff } from './authenticatedRoutePreload';

const lazyImportForRecoveryNextApp = (() => {
  const load = lazyImportForRecovery(() => import('./NextApp'), 'ProductShell/NextApp');
  let promise: ReturnType<typeof load> | undefined;
  return () => promise ??= load();
})();
const NextApp = lazy(lazyImportForRecoveryNextApp);
// Pinpoint Learn web companion (decision L10): a lazy sibling of the SAT
// NextApp, so /learn/* never loads SAT providers, navigation or data.
const LearnApp = lazy(lazyImportForRecovery(() => import('./learn/LearnApp'), 'ProductShell/learn/LearnApp'));
const ChooseRoleApp = lazy(lazyImportForRecovery(() => import('./family/ChooseRoleApp'), 'ProductShell/family/ChooseRoleApp'));
// LoginPage is eager on purpose: /login is AUTH0_CALLBACK_PATH. A React.lazy
// throw on an undefined dynamic import is caught by ProductErrorBoundary,
// which wraps Auth0Provider, so handleRedirectCallback never runs and the
// student is stuck on /login with the callback query still in the URL.

const routeGuardComponents = { ProtectedRoute };

function LatestNonProductionLabel() {
  if (typeof window === 'undefined' || !isLatestProductionHost(window.location.hostname)) return null;
  return (
    <div
      data-testid="latest-non-production-label"
      role="status"
      aria-label="Non-production environment"
      className="fixed top-0 right-0 z-50 rounded-bl bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-900"
    >
      Non-production
    </div>
  );
}

type LegacySatDisposition = 'ordinary' | 'ordinary-or-exam' | 'conditional-exam' | 'exam';

function LegacySatRedirect({ disposition }: { disposition: LegacySatDisposition }) {
  const location = useLocation();
  const rawLegacyTestId = new URLSearchParams(location.search).get('test_id')?.trim() ?? '';
  const hasValidLegacyTestId = /^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/.test(rawLegacyTestId);
  const destination = disposition === 'ordinary'
    || ((disposition === 'ordinary-or-exam' || disposition === 'conditional-exam') && !hasValidLegacyTestId)
    ? '/practice/session'
    : '/practice/exam';
  return <Navigate to={destination} replace />;
}

const legacySatRedirectProps = (disposition: LegacySatDisposition) => () => ({ disposition });

const NextAppRedirect = () => {
  const loc = useLocation();
  const to = loc.pathname.replace(/^\/next\/app/, '') || '/practice';
  return <Navigate to={to + loc.search + loc.hash} replace />;
};

const DevToolbar = ({ isDev }: { isDev: boolean }) => {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [pos, setPos] = useState<{ x: number; y: number } | null>(() => {
    try {
      const s = JSON.parse(localStorage.getItem('devToolbarPos') || '');
      if (s && typeof s.x === 'number') return s;
    } catch { /* ignore */ }
    return null;
  });
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('devToolbarCollapsed') === '1');
  const dragRef = useRef({ on: false, dx: 0, dy: 0, moved: false });

  const onPointerDown = (e: ReactPointerEvent) => {
    if (!(e.target as HTMLElement).closest('[data-dev-grip]')) return;
    const startX = e.clientX;
    const startY = e.clientY;
    const base = pos ?? { x: window.innerWidth - 160, y: window.innerHeight - 56 };
    dragRef.current.on = true;
    dragRef.current.moved = false;
    const move = (ev: PointerEvent) => {
      const nx = base.x + (ev.clientX - startX);
      const ny = base.y + (ev.clientY - startY);
      if (Math.abs(ev.clientX - startX) + Math.abs(ev.clientY - startY) > 3) dragRef.current.moved = true;
      const clampedX = Math.max(4, Math.min(window.innerWidth - 60, nx));
      const clampedY = Math.max(4, Math.min(window.innerHeight - 44, ny));
      setPos({ x: clampedX, y: clampedY });
    };
    const up = () => {
      dragRef.current.on = false;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      setPos((p) => {
        if (p) localStorage.setItem('devToolbarPos', JSON.stringify(p));
        return p;
      });
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const toggleCollapsed = () => setCollapsed((c) => {
    const n = !c;
    localStorage.setItem('devToolbarCollapsed', n ? '1' : '0');
    return n;
  });

  if (!isDev && !isAdmin) return null;

  const posStyle = pos
    ? { position: 'fixed' as const, left: pos.x, top: pos.y, right: 'auto', bottom: 'auto', zIndex: 50, touchAction: 'none' as const }
    : { position: 'fixed' as const, right: 16, bottom: 16, zIndex: 50, touchAction: 'none' as const };

  return (
    <div
      onPointerDown={onPointerDown}
      style={posStyle}
      className="flex items-center bg-white/90 backdrop-blur shadow-lg rounded-lg p-1.5 border border-slate-200"
    >
      <span
        data-dev-grip
        title="Drag to move"
        className="cursor-grab active:cursor-grabbing select-none px-1.5 text-slate-400 text-xs"
        style={{ lineHeight: 1 }}
      >⠿</span>
      <button
        type="button"
        onClick={toggleCollapsed}
        className="px-1.5 py-1 text-xs text-slate-500 hover:text-slate-800"
        title={collapsed ? 'Expand' : 'Collapse'}
      >{collapsed ? '▸' : '▾'}</button>
      {!collapsed && (
        <button
          type="button"
          onClick={async () => {
            if (!window.confirm(
              "This clears this app's data on this device. You'll be signed out and any unsaved work will be lost. Practice already saved to your account is safe. Continue?",
            )) return;
            try {
              await clearSiteData();
              window.alert('✓ Cache cleared. Reloading now.');
            } finally {
              window.location.reload();
            }
          }}
          className="px-3 py-1.5 text-xs font-bold rounded shadow-sm transition-all bg-orange-100 text-orange-900 hover:bg-orange-200 border border-orange-200 flex items-center gap-2"
          title="Aggressively clear all site data"
        >
          <span>🗑️</span> Clear Cache
        </button>
      )}
    </div>
  );
};

const appRouteBindings = {
  'app-login': defineRouteBinding(LoginPage),
  'app-access-pending': defineRouteBinding(AccessPendingRoute),
  'app-callback-failure': defineRouteBinding(ProductRecoveryScreen, () => ({ kind: 'callback-failure' })),
  'app-api-unavailable': defineRouteBinding(ProductRecoveryScreen, () => ({ kind: 'api-unavailable' })),
  'app-offline': defineRouteBinding(ProductRecoveryScreen, () => ({ kind: 'offline' })),
  'app-redirect-loop': defineRouteBinding(ProductRecoveryScreen, () => ({ kind: 'redirect-loop' })),
  'app-next-redirect': defineRouteBinding(NextAppRedirect),
  'app-learn-root': defineRouteBinding(LearnApp),
  'app-next-root': defineRouteBinding(NextApp),
  'app-home': defineRouteBinding(LoginPage),
  'app-protected-layout': defineRouteBinding(Outlet),
  'app-sat-legacy-test': defineRouteBinding(LegacySatRedirect, legacySatRedirectProps('ordinary-or-exam')),
  'app-sat-legacy-study-test': defineRouteBinding(LegacySatRedirect, legacySatRedirectProps('ordinary-or-exam')),
  'app-sat-legacy-tests-synonyms': defineRouteBinding(LegacySatRedirect, legacySatRedirectProps('conditional-exam')),
  'app-sat-legacy-tests-math': defineRouteBinding(LegacySatRedirect, legacySatRedirectProps('conditional-exam')),
  'app-sat-legacy-tests-sentence-completion': defineRouteBinding(LegacySatRedirect, legacySatRedirectProps('conditional-exam')),
  'app-sat-legacy-tests-session': defineRouteBinding(LegacySatRedirect, legacySatRedirectProps('conditional-exam')),
  'app-sat-legacy-tests-reading': defineRouteBinding(LegacySatRedirect, legacySatRedirectProps('conditional-exam')),
  'app-sat-legacy-tests-reading-passage': defineRouteBinding(LegacySatRedirect, legacySatRedirectProps('conditional-exam')),
  'app-sat-legacy-study-clues': defineRouteBinding(LegacySatRedirect, legacySatRedirectProps('ordinary')),
  'app-sat-legacy-mistakes': defineRouteBinding(LegacySatRedirect, legacySatRedirectProps('ordinary')),
  'app-sat-legacy-tests-practice': defineRouteBinding(LegacySatRedirect, legacySatRedirectProps('exam')),
  'candidate-auth-login': defineRouteBinding(LoginPage),
  'candidate-auth-callback': defineRouteBinding(LoginPage),
  'candidate-auth-callback-failure': defineRouteBinding(ProductRecoveryScreen, () => ({ kind: 'callback-failure' })),
  'candidate-auth-redirect-loop': defineRouteBinding(ProductRecoveryScreen, () => ({ kind: 'redirect-loop' })),
  'candidate-account-access-pending': defineRouteBinding(AccessPendingRoute),
  'candidate-account-choose-role': defineRouteBinding(ChooseRoleApp),
  'candidate-status-unavailable': defineRouteBinding(ProductRecoveryScreen, () => ({ kind: 'api-unavailable' })),
  'candidate-status-offline': defineRouteBinding(ProductRecoveryScreen, () => ({ kind: 'offline' })),
  'candidate-sat-root': defineRouteBinding(NextApp),
  'candidate-family-root': defineRouteBinding(NextApp),
  'candidate-learn-root': defineRouteBinding(LearnApp),
};

export function ProductShell({
  isDev = false,
  deploymentProfile = __PINPOINT_DEPLOYMENT_PROFILE__,
}: {
  isDev?: boolean;
  deploymentProfile?: 'legacy-app' | 'www-candidate';
} = {}) {
  const location = useLocation();
  const routeProfile = selectProductRouteProfile(deploymentProfile);
  const routeDecision = classifyBeforeAuth(deploymentProfile, location.pathname);
  useEffect(() => {
    if (routeDecision.kind !== 'product') return;
    const handoff = startAuthenticatedRouteHandoff(location.pathname, lazyImportForRecoveryNextApp);
    if (!handoff) return;
    void handoff.routePromise.catch(() => undefined);
    void handoff.nextAppPromise.catch(() => undefined);
  }, [location.pathname, routeDecision.kind]);

  if (routeDecision.kind !== 'product') {
    return (
      <ProductNavigationProvider deploymentProfile={deploymentProfile}>
        <ProductNotFound />
      </ProductNavigationProvider>
    );
  }

  return (
    <ProductNavigationProvider deploymentProfile={deploymentProfile}>
      <AuthProvider>
        <TooltipProvider>
          <div className="min-h-screen bg-slate-50 font-sans relative">
            <LatestNonProductionLabel />
            <ProductAnalyticsOwner />
            <AuthenticatedRouteHeadingFocus />
            <DevToolbar isDev={isDev} />
            <Suspense fallback={(
              <div data-testid="loading-state" className="min-h-screen bg-slate-50 flex flex-col justify-center items-center p-4 text-center text-sm font-medium text-slate-600" role="status" aria-live="polite">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-4" aria-hidden="true"></div>
                Loading Application...
              </div>
            )}>
              <Routes>{renderRouteManifest(
                routeProfile.shell,
                appRouteBindings,
                routeGuardComponents,
              )}</Routes>
            </Suspense>
          </div>
        </TooltipProvider>
      </AuthProvider>
    </ProductNavigationProvider>
  );
}

export default ProductShell;
